import type { AuthUserType } from '@econtractor/types';

type AccessField =
    | 'prodEdit'
    | 'objectCreate'
    | 'proposalCreate'
    | 'warehouseOps'
    | 'reportsView'
    | 'teamsEdit'
    | 'tasksViewAll'
    | 'toolsBase'
    | 'salesAccess';

export const hasUserAccess = (user: AuthUserType | null | undefined, field: AccessField): boolean => {
    if (!user) return false;
    if (user.admin) return true;
    return !!user.access?.[field];
};

export const isSellerUser = (user: AuthUserType | null | undefined): boolean => {
    if (!user || user.admin) return false;
    return user.access?.role === 'seller';
};

export const canWarehouseOps = (user: AuthUserType | null | undefined): boolean => {
    if (!user) return false;
    if (user.admin) return true;
    if (user.access?.role === 'seller') return false;
    if (user.access?.role !== 'manager') return true;
    return !!user.access?.warehouseOps;
};

/** Адмін або менеджер (кабінет CRM), на відміну від виконавця бригади */
export const isManagerUser = (user: AuthUserType | null | undefined): boolean => {
    if (!user) return false;
    if (user.admin) return true;
    return user.access?.role === 'manager';
};

/** Створювати задачі може адмін, менеджер або продавець */
export const canCreateTasks = (user: AuthUserType | null | undefined): boolean =>
    isManagerUser(user) || isSellerUser(user);

/** Довідники задач і процесів — лише адмін або менеджер */
export const canManageTaskDir = (user: AuthUserType | null | undefined): boolean =>
    isManagerUser(user);

/** Адмін або менеджер з доступом «Доступ до всіх задач» */
export const canViewAllTasks = (user: AuthUserType | null | undefined): boolean => {
    if (!user) return false;
    if (user.admin) return true;
    return user.access?.role === 'manager' && !!user.access?.tasksViewAll;
};

/** Адмін або менеджер з доступом «Доступ до бази інструментів» */
export const canViewToolsBase = (user: AuthUserType | null | undefined): boolean => {
    if (!user) return false;
    if (user.admin) return true;
    return user.access?.role === 'manager' && !!user.access?.toolsBase;
};

/** Адмін або менеджер з доступом «Доступ до продаж» */
export const canViewSales = (user: AuthUserType | null | undefined): boolean => {
    if (!user) return false;
    if (user.admin) return true;
    if (user.access?.role === 'seller') return true;
    return user.access?.role === 'manager' && !!user.access?.salesAccess;
};

/** Продавець завжди. Менеджер — з галочкою «Приховати закупівельні ціни». Адмін бачить ціни. */
export const hidesPurchasePrices = (user: AuthUserType | null | undefined): boolean => {
    if (!user || user.admin) return false;
    if (user.access?.role === 'seller') return true;
    return user.access?.role === 'manager' && !!user.access?.hidePurchasePrices;
};

/** Менеджер бачить і обирає партії за закупівельною ціною. Інакше сервер ділить сам. */
export const splitsStockByPurchasePrice = (
    user: AuthUserType | null | undefined,
    ignoreWarehousePrice: boolean,
): boolean => {
    if (!user || ignoreWarehousePrice) return false;
    if (user.access?.role !== 'manager') return false;
    return !hidesPurchasePrices(user);
};
